import * as React from 'react'
import {
  DataResult,
  DataFilterOptions,
  DataPaginationOptions,
  Box,
  makeStyles,
  Theme,
  createStyles,
  SortInfo,
} from 'cs-kevlar-react'
import { useAuditApi } from 'hooks'
import {
  AuditSearchRequest,
  AuditSearchSortValue,
  AuditEventSearchResult,
  SortDirection,
} from '../../api'
import { asIdentifiable } from 'utils'
import { SidePanelContent } from 'components'
import { EventDetails } from './EventDetails'
import { usePageNotifications } from 'hooks/usePageNotifications'
import { AllOption, SearchForm, SearchOptions } from './SearchForm'
import {
  ColumnMappings,
  Result,
  ResultsTable,
} from 'components/audit/ResultsTable'

const addDays = (date: Date, days: number): Date => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + days)
  return newDate
}

const DefaultSearchOptions: SearchOptions = {
  startDate: addDays(new Date(), -7),
  endDate: new Date(),
  customerAgencyId: AllOption.key,
  licensedAgencyId: AllOption.key,
  userId: AllOption.key,
  eventType: AllOption.key,
}

const DefaultDataResult = (
  pageSize: number | undefined,
): DataResult<Result> => ({
  rows: [],
  totalRows: 0,
  totalFilteredRows: 0,
  pageIndex: 0,
  pageSize: pageSize ?? 0,
  totalPages: 0,
})

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minHeight: 0,
    },
  }),
)

const resolveSearchOption = (value: any) => {
  return value && value !== AllOption.value ? value : undefined
}

export interface AuditComponentProps {}

export const AuditComponent: React.FunctionComponent<AuditComponentProps> = () => {
  const classes = useStyles({})
  const auditApi = useAuditApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const [searchOptions, setSearchOptions] = React.useState<SearchOptions>(
    DefaultSearchOptions,
  )
  const [selectedResult, setSelectedResult] = React.useState<Result | null>(
    null,
  )

  const notifyOnGetDataError = () => {
    dispatchNotification({
      type: 'SetError',
      payload: 'Failed to load audit events.',
    })
  }

  const onSearchOptionsChanged = (options: SearchOptions) => {
    // Copy options to a new ref so that each time search is requested we get data.
    // If we don't copy and a search is requested with the same options, getData won't be re-evaluated.
    setSearchOptions({ ...options })
  }

  const getData = React.useCallback(
    (
      _filterBy: DataFilterOptions | null,
      sortBy: SortInfo<Result> | null,
      pageBy: DataPaginationOptions | null,
    ) => {
      if (searchOptions === DefaultSearchOptions)
        return Promise.resolve(DefaultDataResult(pageBy?.pageSize))

      const request: AuditSearchRequest = {
        startDate: searchOptions.startDate.toDateString(),
        endDate: searchOptions.endDate.toDateString(),
        customerAgencyId: resolveSearchOption(searchOptions.customerAgencyId),
        licensedAgencyId: resolveSearchOption(searchOptions.licensedAgencyId),
        userId: resolveSearchOption(searchOptions.userId),
        eventTypeDescription:
          searchOptions.eventType !== AllOption.value
            ? searchOptions.eventType
            : '',
        sortValue:
          ColumnMappings[sortBy?.columnIndex ?? 0].sortValue ??
          AuditSearchSortValue.EventTimestamp,
        sortDirection:
          sortBy?.direction === 'asc' ? SortDirection.Asc : SortDirection.Desc,
        pageIndex: pageBy?.pageIndex ?? 0,
        pageSize: pageBy?.pageSize ?? 1,
      }

      return auditApi
        .searchAuditEvents(request)
        .then((response) => {
          if (!response || response.status !== 200) {
            notifyOnGetDataError()
            return DefaultDataResult(pageBy?.pageSize)
          }

          const results = response.data.results.map((r) =>
            asIdentifiable(r, r.eventId.toString()),
          )

          return {
            rows: results,
            totalRows: response.data.totalResults,
            totalFilteredRows: null,
            pageIndex: response.data.pageIndex,
            pageSize: response.data.pageSize,
            totalPages: response.data.totalPages,
          } as DataResult<Result>
        })
        .catch((_) => {
          notifyOnGetDataError()
          return DefaultDataResult(pageBy?.pageSize)
        })
    },
    [searchOptions],
  )

  return (
    <React.Fragment>
      <Box className={classes.container}>
        <SearchForm
          initialSearchOptions={DefaultSearchOptions}
          onSearchRequested={onSearchOptionsChanged}
        />

        <ResultsTable
          getData={getData}
          selectedRow={selectedResult}
          onRowSelected={setSelectedResult}
        />
      </Box>

      {!!selectedResult && (
        <SidePanelContent
          anchor="right"
          title="Record Details"
          onClose={() => setSelectedResult(null)}
        >
          <EventDetails event={selectedResult as AuditEventSearchResult} />
        </SidePanelContent>
      )}
    </React.Fragment>
  )
}
